import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Select, Toggle } from '@/components/admin/shared';

/**
 * Inline template selector + Presentation Override toggle.
 * Props:
 *   targetType: 'channel' | 'product' | 'collection' | 'landing'
 *   targetId:   string (required to persist; if missing, control is disabled)
 */
export default function TemplateAssignmentControl({ targetType, targetId }) {
  const [templates, setTemplates] = useState([]);
  const [assignment, setAssignment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const disabled = !targetId;

  useEffect(() => {
    let alive = true;
    api.get(`/admin/templates`, { params: { kind: targetType } })
      .then(({ data }) => { if (alive) setTemplates(data || []); }).catch(() => {});
    if (!disabled) {
      api.get('/admin/template-assignments', { params: { target_type: targetType, target_id: targetId } })
        .then(({ data }) => { if (alive) setAssignment((data && data[0]) || null); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [targetType, targetId, disabled]);

  const options = useMemo(() => [
    { value: '', label: '— Use default layout —' },
    ...templates.map((t) => ({ value: t.id, label: `${t.label}${t.is_default ? ' (default)' : ''}${t.is_builtin ? '' : ' · custom'}` })),
  ], [templates]);

  const save = async (patch) => {
    if (disabled) return;
    const next = { ...(assignment || { presentation_override: true, template_id: '' }), ...patch };
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/template-assignments/${targetType}/${targetId}`,
        { template_id: next.template_id, presentation_override: next.presentation_override, settings_override: next.settings_override || {} });
      if (data?.removed) setAssignment(null);
      else setAssignment(data);
      setMsg('Template updated.');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="pg-admin-card rounded-[var(--pg-radius-md)] p-4 space-y-3" data-testid={`template-assign-${targetType}`}>
      <div className="flex items-center justify-between">
        <p className="pg-label text-[10px]">TEMPLATE</p>
        {disabled && <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">Save the item first to enable templates.</p>}
      </div>
      <Select
        label="Assigned template"
        value={assignment?.template_id || ''}
        onChange={(v) => save({ template_id: v })}
        options={options}
      />
      <Toggle label="Presentation Override (use assigned template on the public site)"
              value={assignment?.presentation_override !== false}
              onChange={(v) => save({ presentation_override: v })} />
      {busy && <p className="text-[11px] text-[var(--pg-ink-faint)] font-mono">Saving…</p>}
      {msg && !busy && <p className="text-[11px] text-[var(--pg-emerald-2)] font-mono">{msg}</p>}
    </div>
  );
}
